/* One shared cart for product forms, quick add, bulk quantities and header links. */
class EvergreenMiniCart extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.dialog = this.querySelector('dialog');
    this.currentItemCount = Number(this.dataset.itemCount) || 0;
    this.revision = 0;
    this.pending = 0;
    this.dismissedNotices = new Set();
    this.checkoutDisabledStates = new Map();
    this.queue = Promise.resolve();
    this.onDocumentClick = this.handleDocumentClick.bind(this);
    this.onDocumentSubmit = this.handleDocumentSubmit.bind(this);
    this.onExternalAdd = () => this.refreshCart().then(() => this.open()).catch((error) => this.showSyncError(error));
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('submit', this.onDocumentSubmit);
    document.addEventListener('cart:item-added', this.onExternalAdd);
    this.addEventListener('click', (event) => this.handleClick(event));
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-cart-quantity]')) this.changeFromInput(event.target);
    });
    this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); });
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.dialog.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]')).filter((node) => node.tabIndex >= 0 && node.getClientRects().length && !node.closest('[hidden]'));
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    });
    this.dialog.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
    });
    this.dialog.addEventListener('close', () => this.afterClose());
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'mini-cart') return;
      const previousCount = this.currentItemCount;
      const state = event.cartData;
      const increased = event.source === 'quick-add' && state?.item_count > previousCount;
      // External mutations can finish while our queue is busy. Read the latest
      // cart when this refresh runs instead of replaying an older event payload.
      this.refreshCart().then(() => {
        if (increased) this.open(document.activeElement);
      }).catch((error) => this.showSyncError(error));
    });
    this.syncState();
    // The page already has a current, server-rendered cart. Only make an
    // initial request when the gift actually needs adding/removing/normalizing.
    if (this.getRenderedGiftAction()) this.refreshCart({ background: true });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('submit', this.onDocumentSubmit);
    document.removeEventListener('cart:item-added', this.onExternalAdd);
    this.unsubscribe?.();
    if (this.dialog?.open) this.close();
  }

  getSectionsToRender() {
    return [
      { id: 'header-mini-cart-content', section: 'header-mini-cart-content' },
      { id: 'header-mini-cart-footer', section: 'header-mini-cart-footer' },
    ];
  }

  isOpen() { return this.dialog.open; }

  open(opener) {
    if (this.isOpen()) return;
    this.openedBy = opener instanceof HTMLElement ? opener : document.activeElement;
    // Clear the theme's older focus trap before using the native modal dialog.
    removeTrapFocus();
    this.dialog.showModal();
    document.body.classList.add('evergreen-cart-open');
    document.querySelectorAll('[data-mini-cart-open]').forEach((node) => node.setAttribute('aria-expanded', 'true'));
    this.querySelector('[data-mini-cart-close]').focus();
    publish(PUB_SUB_EVENTS.headerCartOpen);
  }

  close() {
    if (!this.dialog.open) return;
    this.dialog.close();
    this.afterClose();
  }

  afterClose() {
    if (this.dialog.open || !document.body.classList.contains('evergreen-cart-open')) return;
    document.body.classList.remove('evergreen-cart-open');
    document.querySelectorAll('[data-mini-cart-open]').forEach((node) => node.setAttribute('aria-expanded', 'false'));
    if (this.openedBy?.isConnected && !this.contains(this.openedBy)) this.openedBy.focus();
    publish(PUB_SUB_EVENTS.headerCartClose);
  }

  handleDocumentClick(event) {
    const opener = event.target.closest('[data-mini-cart-open]');
    if (!opener || event.defaultPrevented || event.button > 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    this.open(opener);
    this.refreshCart({ background: true });
  }

  handleDocumentSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || event.defaultPrevented || form.closest('product-form') || form.hasAttribute('data-native-cart-submit')) return;
    const action = new URL(form.action, location.href);
    const cartCheckout = action.origin === location.origin && /\/cart\/?$/.test(action.pathname) &&
      (event.submitter?.name === 'checkout' || form.id === 'EvergreenCartCheckout');
    if (cartCheckout && this.resumingCheckout !== form && (this.pending || this.shouldSyncGift())) {
      event.preventDefault();
      if (this.checkoutPending) return;
      this.checkoutPending = true;
      this.refreshCart({ checkout: true }).then(async () => {
        // Wait for edits made during the check, including edits that arrive
        // while the cart-page form is being refreshed.
        while (true) {
          while (this.pending) await this.queue;
          if (this.giftSyncFailed) throw this.giftSyncFailed;
          const revision = this.revision;
          try { await this.syncCartPage({ required: true }); }
          catch (error) { if (!this.pending && revision === this.revision) throw error; }
          if (!this.pending && revision === this.revision) break;
        }
        const currentForm = (form.id && document.getElementById(form.id)) || form;
        const checkout = currentForm.querySelector('[name="checkout"]') ||
          Array.from(currentForm.elements).find((element) => element.name === 'checkout');
        this.resumingCheckout = currentForm;
        try { currentForm.requestSubmit(checkout); }
        finally { this.resumingCheckout = null; }
      }).catch((error) => { this.showSyncError(error); this.open(); })
        .finally(() => { this.checkoutPending = false; });
      return;
    }
    if (action.origin !== location.origin || !/\/cart\/add(?:\.js)?\/?$/.test(action.pathname)) return;
    // Dynamic checkout buttons keep their own accelerated checkout flow.
    if (event.submitter?.name === 'checkout' || event.submitter?.closest('.shopify-payment-button')) return;
    event.preventDefault();
    const button = event.submitter;
    if (button?.disabled) return;
    const formData = new FormData(form);
    if (button) button.disabled = true;
    this.add(formData, button).catch((error) => { this.open(button); this.showError(error.message); }).finally(() => { if (button?.isConnected) button.disabled = false; });
  }

  handleClick(event) {
    if (event.target.closest('[data-mini-cart-close]')) { this.close(); return; }
    const dismiss = event.target.closest('[data-dismiss-preorder]');
    if (dismiss) {
      const notice = dismiss.closest('[data-preorder-notice]');
      this.dismissedNotices.add(notice.dataset.preorderNotice);
      notice.hidden = true;
      this.querySelector('[data-mini-cart-close]').focus();
      return;
    }
    if (this.pending) return;
    const add = event.target.closest('[data-cart-recommendation]');
    if (add) {
      const form = new FormData();
      form.set('id', add.dataset.cartRecommendation);
      form.set('quantity', add.dataset.quantity || '1');
      this.add(form, add).catch((error) => this.showError(error.message));
      return;
    }
    const line = event.target.closest('[data-cart-line]');
    if (!line) return;
    const input = line.querySelector('[data-cart-quantity]');
    if (event.target.closest('[data-cart-remove]')) { this.change(line.dataset.key, 0, this.readLineIdentity(line)); return; }
    const step = event.target.closest('[data-quantity-step]');
    if (step) {
      const value = Number(input.value) + Number(step.dataset.quantityStep) * (Number(input.step) || 1);
      input.value = value < Number(input.dataset.min) ? (Number(step.dataset.quantityStep) < 0 ? 0 : input.dataset.min) : value;
      this.changeFromInput(input);
    }
  }

  changeFromInput(input) {
    if (!input.checkValidity() || !Number.isInteger(Number(input.value))) { input.reportValidity(); return; }
    const quantity = Number(input.value);
    if (quantity > 0 && quantity < Number(input.dataset.min)) {
      input.value = input.dataset.min;
    }
    const line = input.closest('[data-cart-line]');
    this.change(line.dataset.key, Number(input.value), this.readLineIdentity(line));
  }

  readLineIdentity(line) {
    return line?.dataset.cartIdentity ? JSON.parse(line.dataset.cartIdentity) : undefined;
  }

  setBusy(busy) {
    this.dialog.setAttribute('aria-busy', String(busy));
    this.querySelectorAll('[data-cart-recommendation], [data-quantity-step], [data-cart-remove], [data-cart-quantity], [name="checkout"]').forEach((node) => { node.disabled = busy; });
    document.querySelectorAll('[name="checkout"]').forEach((button) => {
      if (this.contains(button) || !button.form || !/\/cart\/?$/.test(new URL(button.form.action, location.href).pathname)) return;
      if (busy) {
        if (!this.checkoutDisabledStates.has(button)) this.checkoutDisabledStates.set(button, button.disabled);
        button.disabled = true;
      }
    });
    if (!busy) {
      this.checkoutDisabledStates.forEach((disabled, button) => { button.disabled = disabled; });
      this.checkoutDisabledStates.clear();
    }
  }

  runMutation(task, { clearError = true } = {}) {
    this.pending += 1;
    this.setBusy(true);
    const operation = this.queue.then(() => {
      // Enqueuing a later update must not invalidate the read already running.
      this.revision += 1;
      if (clearError) this.showError('');
      return task();
    }).finally(() => {
      this.pending -= 1;
      this.setBusy(this.pending > 0);
      if (!this.pending && this.isOpen() && !this.dialog.contains(document.activeElement)) this.querySelector('[data-mini-cart-close]').focus();
    });
    this.queue = operation.catch(() => {});
    return operation;
  }

  async request(url, options = {}, format = 'json') {
    const readOnly = !options.method || options.method.toUpperCase() === 'GET';
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await fetch(url, options);
        const result = format === 'text' ? await response.text() : await response.json();
        if (!response.ok || (format === 'json' && (result.status || result.errors))) {
          const message = result.description || result.errors || result.message;
          const error = new Error(typeof message === 'string' ? message : this.dataset.error);
          error.status = response.status;
          throw error;
        }
        return result;
      } catch (error) {
        // Reads are safe to retry. Never blindly repeat a POST: Shopify may
        // have committed it before the response was interrupted.
        if (!readOnly || attempt === 1 || (error.status && error.status !== 429 && error.status < 500)) throw error;
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    }
  }

  notifyCart(event, data) {
    try { publish(event, data); }
    catch (error) { console.error('Cart subscriber could not refresh:', error); }
  }

  async add(formData, opener, source = 'MiniCart') {
    return this.runMutation(async () => {
      formData.set('sections', this.getSectionsToRender().map((section) => section.section).join(','));
      formData.set('sections_url', location.pathname);
      const result = await this.request(this.dataset.addUrl, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
      // A successful add must never be reported as failed merely because sections failed.
      const settled = await this.renderContents(result).catch((error) => { this.showSyncError(error); return result; });
      const quickView = opener?.closest?.('quick-view-drawer');
      if (quickView) {
        this.openedBy = quickView.openedBy;
        quickView.close();
      }
      this.open(quickView?.openedBy || opener);
      this.notifyCart(PUB_SUB_EVENTS.cartItemAdd, { source, items: result.items || [result] });
      this.notifyCart(PUB_SUB_EVENTS.cartUpdate, { source: 'mini-cart', cartData: settled });
      await this.syncCartPage().catch(() => {});
      return result;
    });
  }

  change(key, quantity, identity) {
    if (!Number.isInteger(quantity) || quantity < 0) return Promise.resolve();
    return this.runMutation(async () => {
      try {
        if (identity) {
          // Discount-dependent keys may have changed while a gift was added.
          // Resolve the purchased line at execution time, in either cart view.
          const cart = await this.request(this.dataset.cartUrl.replace(/\/$/, '') + '.js', {
            headers: { Accept: 'application/json' }, cache: 'no-store',
          });
          const item = cart.items.find((line) => line.key === key && this.matchesLineIdentity(line, identity)) ||
            cart.items.find((line) => this.matchesLineIdentity(line, identity));
          if (!item) {
            if (quantity === 0) {
              await this.renderContents(cart);
              await this.syncCartPage().catch(() => {});
              return;
            }
            throw new Error(this.dataset.error);
          }
          key = item.key;
        }
        const result = await this.request(this.dataset.changeUrl, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: this.getSectionsToRender().map((s) => s.section), sections_url: location.pathname }),
        });
        const settled = await this.renderContents(result).catch((error) => { this.showSyncError(error); return result; });
        this.notifyCart(PUB_SUB_EVENTS.cartUpdate, { source: 'mini-cart', cartData: settled });
        await this.syncCartPage().catch(() => {});
        this.notifyCart(quantity === 0 ? PUB_SUB_EVENTS.cartItemRemove : PUB_SUB_EVENTS.cartItemQuantityUpdate, { source: 'MiniCart', cart: settled });
        const updatedItem = identity && settled.items?.find((item) => this.matchesLineIdentity(item, identity));
        const updatedKey = updatedItem?.key || key;
        const row = Array.from(this.querySelectorAll('[data-cart-line]')).find((node) => node.dataset.key === updatedKey);
        if (this.isOpen()) (row?.querySelector('[data-cart-quantity]') || this.querySelector('[data-mini-cart-close]'))?.focus();
      } catch (error) {
        // Keep recovery in the queue, too, so it cannot overwrite the next edit.
        await this.updateSectionContents().catch(() => {});
        this.showError(error.message);
        if (identity) document.querySelector('cart-items')?.setError(error.message);
      }
    });
  }

  matchesLineIdentity(item, identity) {
    const properties = Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b));
    const expectedProperties = Object.entries(identity.properties || {}).sort(([a], [b]) => a.localeCompare(b));
    return String(item.variant_id) === String(identity.variantId) &&
      String(item.selling_plan_allocation?.selling_plan?.id || '') === String(identity.sellingPlanId || '') &&
      JSON.stringify(properties) === JSON.stringify(expectedProperties);
  }

  async renderContents(state) {
    this.revision += 1;
    let renderError;
    if (!this.applySections(state.sections)) {
      try { await this.updateSectionContents(); }
      catch (error) { renderError = error; }
    }
    // A missing HTML section must not stop the actual gift from being updated.
    const settled = await this.reconcileGift(state, { forceRead: Boolean(renderError) });
    if (renderError && !this.giftChanged) throw renderError;
    const errorKind = this.querySelector('[data-mini-cart-error]')?.dataset.errorKind;
    if (errorKind === 'refresh' || errorKind === 'gift') this.showError('');
    return settled || state;
  }

  refreshCart({ background = false, checkout = false } = {}) {
    if (background && this.pending) return Promise.resolve();
    const operation = this.runMutation(async () => {
      const state = await this.renderContents({});
      // Read-only opening refreshes don't need to make every product card fetch
      // itself again. Notify other components only when a gift changed the cart.
      if (this.giftChanged) this.notifyCart(PUB_SUB_EVENTS.cartUpdate, { source: 'mini-cart', cartData: state });
      // Checkout refreshes its form once the entire queue has drained.
      if (!checkout && this.giftChanged) await this.syncCartPage().catch(() => {});
      return state;
    }, { clearError: !background });
    // Background reads preserve the usable server-rendered cart on failure.
    // Checkout still performs a required check before submitting.
    return background ? operation.catch(() => undefined) : operation;
  }

  readGiftState() {
    const script = this.querySelector('[data-cart-gift-state]');
    return script ? JSON.parse(script.textContent) : null;
  }

  shouldSyncGift() {
    const config = this.readGiftState();
    return Boolean(config && (config.enabled || config.gifts?.length));
  }

  getRenderedGiftAction(config = this.readGiftState()) {
    if (!config || !Number.isFinite(config.subtotal) || !Array.isArray(config.gifts)) return undefined;
    return this.getGiftAction({ items: [
      { original_line_price: config.subtotal },
      ...config.gifts.map((gift) => ({ ...gift, variant_id: gift.variantId, properties: { _evergreen_free_gift: 'staff-pick' } })),
    ] }, config);
  }

  getGiftAction(cart, config) {
    const gifts = cart.items.filter((item) => item.properties?._evergreen_free_gift === 'staff-pick');
    const subtotal = cart.items.reduce((total, item) =>
      total + (item.properties?._evergreen_free_gift === 'staff-pick' ? 0 : item.original_line_price), 0);
    const threshold = Number(config.threshold);
    const qualifies = config.enabled && config.variantId && Number.isFinite(threshold) && threshold > 0 && subtotal >= threshold;
    const gift = qualifies ? gifts.find((item) => String(item.variant_id) === String(config.variantId)) : null;
    const extra = gifts.find((item) => item !== gift);
    if (extra) return { type: 'change', key: extra.key, quantity: 0 };
    if (gift && gift.quantity !== 1) return { type: 'change', key: gift.key, quantity: 1 };
    if (qualifies && !gift && config.available) return { type: 'add', variantId: String(config.variantId) };
    return null;
  }

  async reconcileGift(state, { forceRead = false } = {}) {
    this.giftChanged = false;
    const config = this.readGiftState();
    if (!config || (!config.enabled && !config.gifts?.length)) {
      this.giftSyncFailed = null;
      return null;
    }
    const hasCart = Array.isArray(state?.items) && Number.isFinite(state.item_count);
    if (!hasCart && !forceRead && this.getRenderedGiftAction(config) === null) {
      this.giftSyncFailed = null;
      return null;
    }
    const cartUrl = this.dataset.cartUrl.replace(/\/$/, '') + '.js';
    const readCart = () => this.request(cartUrl, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    try {
      let cart = hasCart ? state : await readCart();
      let sections;
      // Each pass removes an unwanted line, fixes a quantity, or adds one gift.
      // Read the resulting cart each time: discounts can change all line keys.
      const limit = cart.items.length + 2;
      for (let attempt = 0; attempt < limit; attempt += 1) {
        const action = this.getGiftAction(cart, config);
        if (!action) {
          if (sections) {
            this.revision += 1;
            if (!this.applySections(sections)) await this.updateSectionContents();
          }
          this.giftSyncFailed = null;
          return cart;
        }
        const body = {
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: location.pathname,
        };
        if (action.type === 'add') {
          body.items = [{ id: action.variantId, quantity: 1, properties: { _evergreen_free_gift: 'staff-pick' } }];
        } else {
          body.id = action.key;
          body.quantity = action.quantity;
        }
        let result;
        try {
          result = await this.request(action.type === 'add' ? this.dataset.addUrl : this.dataset.changeUrl, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body),
          });
        } catch (error) {
          if (error.name === 'TypeError' || error.name === 'SyntaxError' || error.status === 429 || error.status >= 500) {
            // The response may have been lost after Shopify saved the gift.
            // Confirm current contents before deciding that synchronization failed.
            const current = await readCart();
            if (!this.getGiftAction(current, config)) {
              this.giftChanged = true;
              this.giftSyncFailed = null;
              await this.updateSectionContents();
              return current;
            }
          }
          if (action.type !== 'add' || error.status !== 422) throw error;
          cart = await readCart();
          // Stock can run out, including when the shopper buys the last unit.
          // A confirmed rejected addition with no gift line can safely continue.
          if (cart.items.some((item) => item.properties?._evergreen_free_gift === 'staff-pick')) throw error;
          await this.updateSectionContents();
          const status = this.querySelector('[data-cart-gift-status]');
          if (status) status.textContent = this.dataset.giftUnavailable;
          this.giftSyncFailed = null;
          return cart;
        }
        sections = result.sections || {};
        this.giftChanged = true;
        // add.js only returns the added lines; change.js returns the full cart.
        cart = action.type === 'add' ? await readCart() : result;
      }
      throw new Error(this.dataset.giftError);
    } catch (error) {
      if (error.kind === 'refresh') {
        this.giftSyncFailed = null;
        throw error;
      }
      // A failed gift request must not imply the shopper's own add failed.
      const giftError = new Error(this.dataset.giftError, { cause: error });
      giftError.kind = 'gift';
      this.giftSyncFailed = giftError;
      throw giftError;
    }
  }

  showSyncError(error) {
    if (error?.kind === 'gift') {
      this.showError(error.message, 'gift');
      const status = this.querySelector('[data-cart-gift-status]');
      if (status) status.textContent = error.message;
    } else {
      this.showError(this.dataset.refreshError || this.dataset.error, 'refresh');
    }
  }

  applySections(sections) {
    const replacements = this.getSectionsToRender().map((section) => {
      const source = sections?.[section.section];
      if (typeof source !== 'string') return null;
      const parsed = new DOMParser().parseFromString(source, 'text/html').querySelector('.shopify-section');
      return parsed ? { element: this.querySelector('#' + section.id), html: parsed.innerHTML } : null;
    });
    if (replacements.some((replacement) => !replacement?.element)) return false;
    const scroll = this.querySelector('.evergreen-cart-scroll').scrollTop;
    const consent = this.querySelector('.evergreen-cart-consent input')?.checked;
    replacements.forEach(({ element, html }) => { element.innerHTML = html; });
    this.querySelector('.evergreen-cart-scroll').scrollTop = scroll;
    if (consent) { const checkbox = this.querySelector('.evergreen-cart-consent input'); if (checkbox) checkbox.checked = true; }
    this.syncState();
    this.setBusy(this.pending > 0);
    if (this.querySelector('[data-mini-cart-error]')?.dataset.errorKind === 'refresh') this.showError('');
    return true;
  }

  async updateSectionContents({ background = false } = {}) {
    // An opening refresh must not supersede a cart mutation already in flight.
    if (background && this.pending) return;
    const revision = ++this.revision;
    const url = new URL(this.dataset.cartUrl, location.origin);
    url.searchParams.set('sections', this.getSectionsToRender().map((s) => s.section).join(','));
    try {
      const result = await this.request(url.toString(), { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (revision !== this.revision) return;
      if (this.applySections(result)) return;
      // Shopify can return null for a section while the cart mutation succeeds.
      // Recover both drawer fragments from the normal cart page in that case.
      const html = await this.request(this.dataset.cartUrl, { headers: { Accept: 'text/html' }, cache: 'no-store' }, 'text');
      if (revision !== this.revision) return;
      const page = new DOMParser().parseFromString(html, 'text/html');
      const sections = {};
      this.getSectionsToRender().forEach((section) => {
        const source = page.getElementById(section.id);
        if (source) sections[section.section] = '<div class="shopify-section">' + source.innerHTML + '</div>';
      });
      if (this.applySections(sections)) return;
      throw new Error(this.dataset.refreshError || this.dataset.error);
    } catch (cause) {
      // A late failure from an older read must not overwrite a newer success.
      if (revision !== this.revision || background) return;
      const error = new Error(this.dataset.refreshError || this.dataset.error, { cause });
      error.kind = 'refresh';
      throw error;
    }
  }

  syncState() {
    const state = this.querySelector('[data-mini-cart-state]');
    if (!state) return;
    this.currentItemCount = Number(state.dataset.itemCount) || 0;
    this.querySelector('[data-mini-cart-count]').textContent = this.currentItemCount;
    this.querySelector('#header-mini-cart-footer').hidden = this.currentItemCount === 0;
    document.querySelectorAll('.head-slot-cart-link').forEach((link) => {
      let count = link.querySelector('.head-slot-cart-link-quantity');
      if (!count && link.querySelector('.head-slot-cart-link-wrap')) {
        count = document.createElement('span'); count.className = 'head-slot-cart-link-quantity';
        link.querySelector('.head-slot-cart-link-wrap').append(count);
      }
      if (count) { count.textContent = this.currentItemCount; count.hidden = this.currentItemCount === 0; }
      const total = link.querySelector('[data-cart-total]');
      if (total) { total.textContent = '(' + state.dataset.total + ')'; total.hidden = this.currentItemCount === 0; }
    });
    this.querySelectorAll('[data-preorder-notice]').forEach((notice) => { notice.hidden = this.dismissedNotices.has(notice.dataset.preorderNotice); });
    this.querySelector('[data-mini-cart-status]').textContent = this.querySelector('#EvergreenCartTitle').textContent;
  }

  async syncCartPage({ required = false } = {}) {
    const cartPage = document.querySelector('cart-items');
    if (!cartPage) return;
    const consent = document.getElementById('checkbox-cart-policy-consent')?.checked;
    const revision = this.revision;
    const sections = cartPage.getSectionsToRender().filter((section) => section.id !== 'header' && section.id !== 'mobile-dock');
    const url = new URL(this.dataset.cartUrl, location.origin);
    url.searchParams.set('sections', [...new Set(sections.map((section) => section.section))].join(','));
    const result = await this.request(url.toString(), { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (revision !== this.revision) {
      if (required) throw new Error(this.dataset.error);
      return;
    }
    for (const section of sections) {
      const container = document.getElementById(section.id);
      const source = result[section.section];
      if (!container || typeof source !== 'string') {
        if (required) throw new Error(this.dataset.error);
        continue;
      }
      const parsed = new DOMParser().parseFromString(source, 'text/html').querySelector(section.selector || '.shopify-section');
      const target = section.selector ? container.querySelector(section.selector) : container;
      if (parsed && target) target.innerHTML = parsed.innerHTML;
      else if (required) throw new Error(this.dataset.error);
    }
    cartPage.classList.toggle('is-empty', this.currentItemCount === 0);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', this.currentItemCount === 0);
    const checkbox = document.getElementById('checkbox-cart-policy-consent');
    if (checkbox && consent !== undefined) checkbox.checked = consent;
    if (this.giftSyncFailed) cartPage.setError(this.giftSyncFailed.message);
  }

  showError(message = this.dataset.error, kind = 'mutation') {
    const error = this.querySelector('[data-mini-cart-error]');
    error.dataset.errorKind = message ? kind : '';
    error.textContent = message || '';
    error.hidden = !message;
  }
}
customElements.define('mini-cart', EvergreenMiniCart);
